const CONNECT_PATH = '/admin/settings/integrations/docusign/connect/';
const STATUS_PATH = '/admin/settings/integrations/docusign/status/';
const ERROR_PARAM = 'docusign_error';

let _dsConnected = false;
let _dsAccountLabel = '';
let _dsError = null;
let _initialized = false;
const _statusListeners = [];

// gettext() calls are deferred inside this function (not evaluated at module load)
// since this module can be required before the page's i18n bundle has run.
function getErrorCopy(code) {
	switch (code) {
		case 'access_denied':
			return gettext('Connection cancelled. Click Connect to Docusign to try again.');
		case 'token_exchange_failed':
			return gettext('Unable to connect your Docusign account. Please try again.');
		case 'insufficient_permissions':
			return gettext(
				'Your Docusign account does not support this integration. Upgrade your Docusign plan or contact Docusign support.',
			);
		case 'unsupported_plan':
			return gettext(
				'Free and trial Docusign accounts cannot be used with this integration. Please connect using a paid Docusign account.',
			);
		case 'rate_limited':
			return gettext('Docusign is busy. Try again in a few minutes.');
		case 'token_expired':
			return gettext(
				'Your Docusign connection expired. Connect again from the Docusign Signature question.',
			);
		case 'unknown':
			return gettext('Unable to connect your Docusign account. Please try again.');
		case null:
		case undefined:
			return '';
		default:
			return gettext('Unable to connect your Docusign account. Please try again.');
	}
}

// Reads docusign_error once per page load — every real trigger for this param is
// itself a fresh page load (an OAuth redirect back), so there's no need to poll for
// it. Strips the param so a manual refresh doesn't re-show a stale banner.
function readErrorFromUrl() {
	try {
		const params = new URLSearchParams(window.location.search);
		const error = params.get(ERROR_PARAM);
		if (!error) {
			return;
		}
		_dsError = error;
		params.delete(ERROR_PARAM);
		const query = params.toString();
		const newUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
		window.history.replaceState(null, '', newUrl);
	} catch (e) {
		// Malformed URL / no history API support — never let this block init.
	}
}

function getConnectUrl(source) {
	// Always compute dynamically so we capture the current hash fragment
	// (e.g. #round/1767/task/1330/form/2174). Using DOCUSIGN_CONNECT_URL from
	// the template or freezing the URL at init() time both lose the hash because
	// request.path never includes it.
	const next = encodeURIComponent(
		window.location.pathname + window.location.search + window.location.hash
	);
	const sourceParam = source ? `&source=${encodeURIComponent(source)}` : '';
	return `${CONNECT_PATH}?next=${next}${sourceParam}`;
}

function applyPanelState(panel) {
	const disconnected = panel.querySelector('.docusign-connect-disconnected');
	const connected = panel.querySelector('.docusign-connect-connected');
	const accountEl = panel.querySelector('.docusign-connect-account');

	if (_dsConnected) {
		if (disconnected) {
			disconnected.style.display = 'none';
		}
		if (connected) {
			connected.style.display = '';
		}
		if (accountEl) {
			accountEl.textContent = _dsAccountLabel;
		}
	} else {
		if (disconnected) {
			disconnected.style.display = '';
		}
		if (connected) {
			connected.style.display = 'none';
		}
		const btn = panel.querySelector('.docusign-sign-btn');
		if (btn && btn.tagName === 'A') {
			btn.setAttribute('href', getConnectUrl());
		}
	}
}

function refreshPanels() {
	document.querySelectorAll('.docusign-connection-panel').forEach(applyPanelState);
}

function fetchStatus() {
	return fetch(STATUS_PATH, {
		credentials: 'same-origin',
	})
		.then((r) => r.json())
		.then((d) => {
			_dsConnected = !!d.connected;
			_dsAccountLabel = d.display_name || d.account_email || '';
			// A fresh URL-param error (e.g. access_denied right after redirect) takes
			// precedence over a null d.error on the first poll; d.error otherwise carries
			// the backend-detected reason (e.g. token_expired) for an existing disconnect.
			_dsError = _dsConnected ? null : (d.error || _dsError);
			refreshPanels();
			_statusListeners.forEach((cb) => cb(getState()));
		})
		.catch(() => {
			// Still notify listeners on failure (with the current default state) so
			// UI that hides itself until the first resolution doesn't stay hidden forever.
			refreshPanels();
			_statusListeners.forEach((cb) => cb(getState()));
		});
}

function onStatusChange(callback) {
	_statusListeners.push(callback);
	return () => {
		const index = _statusListeners.indexOf(callback);
		if (index !== -1) {
			_statusListeners.splice(index, 1);
		}
	};
}

function init() {
	if (_initialized) {
		refreshPanels();
		return;
	}
	_initialized = true;

	// docusign_error is read once per page load — every real trigger for it is itself
	// a fresh page load (an OAuth redirect back) — before the first fetchStatus() call
	// so its precedence logic (see fetchStatus) sees it already set.
	readErrorFromUrl();

	// Hide all panels until the first /status/ call resolves so we don't briefly
	// flash the "not connected" UI before we know the real state.
	document.querySelectorAll('.docusign-connection-panel').forEach((p) => {
		p.style.visibility = 'hidden';
	});

	fetchStatus().finally(() => {
		document.querySelectorAll('.docusign-connection-panel').forEach((p) => {
			p.style.visibility = '';
		});
	});
}

function getState() {
	return { connected: _dsConnected, accountLabel: _dsAccountLabel, error: _dsError };
}

module.exports = {
	init,
	getConnectUrl,
	getState,
	getErrorCopy,
	refreshPanels,
	fetchStatus,
	onStatusChange,
	CONNECT_PATH,
};
